import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowUpRight, Clock, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (v) => `LKR ${Number(v || 0).toLocaleString()}`;

export const LandingPackages = () => {
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    axios.get(`${API}/packages`).then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  if (packages.length === 0) return null;

  return (
    <section id="packages" data-testid="packages-section" className="relative bg-forest py-28 md:py-40 border-t border-cream/10">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-24"
        >
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-amber mb-6" data-testid="packages-label">
              Day Packages
            </p>
            <h2 className="font-display text-4xl md:text-6xl tracking-tight text-cream leading-[1.05] max-w-2xl">
              Pick your adventure. <span className="italic text-amber">Book it today.</span>
            </h2>
          </div>
          <p className="max-w-sm text-base text-cream/70 leading-relaxed">
            Transparent per-person pricing, everything included. Reserve your date online — our team confirms within hours.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-cream/10 border border-cream/10">
          {packages.map((p, i) => (
            <motion.article
              key={p.id}
              data-testid={`package-card-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className="group relative bg-forest p-10 md:p-14 hover:bg-moss transition-colors duration-500 flex flex-col"
            >
              <div className="flex items-center justify-between mb-8">
                <span className="text-[10px] tracking-[0.25em] uppercase text-amber border border-amber/40 px-3 py-1">
                  {p.type}
                </span>
                <span className="flex items-center gap-1.5 text-xs text-cream/50">
                  <Clock size={13} className="text-amber" /> {p.duration}
                </span>
              </div>

              <h3 className="font-display text-3xl md:text-4xl tracking-tight text-cream group-hover:text-amber transition-colors duration-500 mb-4">
                {p.name}
              </h3>
              <p className="text-sm text-cream/60 leading-relaxed mb-8">{p.description}</p>

              <div className="mb-8">
                <span className="font-display text-5xl text-amber">{money(p.base_price)}</span>
                <span className="text-xs text-cream/50 tracking-[0.15em] uppercase ml-2">per person</span>
              </div>

              {p.inclusions?.length > 0 && (
                <ul className="space-y-2 mb-10 flex-1" data-testid={`package-inclusions-${p.id}`}>
                  {p.inclusions.map((inc) => (
                    <li key={inc} className="flex items-start gap-3 text-sm text-cream/75">
                      <span className="w-1.5 h-1.5 rotate-45 bg-amber mt-1.5 shrink-0" />
                      {inc}
                    </li>
                  ))}
                </ul>
              )}

              <a
                data-testid={`package-book-${p.id}`}
                href={`/book?package=${p.id}`}
                className="mt-auto flex items-center justify-center gap-2 bg-amber text-night text-xs tracking-[0.25em] uppercase font-semibold px-8 py-5 hover:bg-cream transition-colors duration-300"
              >
                Book This Package <ArrowUpRight size={16} />
              </a>
            </motion.article>
          ))}
        </div>

        <p className="mt-8 flex items-center gap-2 text-xs text-cream/40 tracking-wide" data-testid="packages-note">
          <Users size={14} className="text-amber" /> Groups of any size welcome — custom packages available on the booking page.
        </p>
      </div>
    </section>
  );
};
